import { useQuery } from "react-query";
import {
  getAllInsuranceCodesAsync,
  getClientInsurancesAsync,
  getInsurancePlanByIdAsync,
} from "api/insurance";
import {
  GET_ALL_INSURANCE_CODES,
  GET_CLIENT_INSURANCE_PLAN_BY_ID,
  GET_CLIENT_INSURANCES,
  STALE_TIME_5_MINUTES,
  STALE_TIME_60_MINUTES,
} from "@constants/reactQueryKeys";

interface CptCodeInfo {
  id: number;
  code: string;
  description: string;
  insurance_code: string;
}

export interface ClientInsurancePlanCptCodes {
  assessment: CptCodeInfo | null;
  direct_therapy: CptCodeInfo | null;
  caregiver_mediated: CptCodeInfo | null;
  caregiver_training: CptCodeInfo | null;
  protocol_modification_direct: CptCodeInfo | null;
  protocol_modification_indirect: CptCodeInfo | null;
  approve_assessment_billing_at: string | null;
}

/**
 * Fetches the CPT code mappings for a client's primary active insurance plan.
 * Returns null cptCodes if the client has no active primary insurance plan,
 * or if none of the therapy types have CPT codes configured.
 */
const useGetClientInsurancePlanCptCodes = ({
  clientId,
}: {
  clientId: string | undefined | null;
}) => {
  // Step 1: Get client's insurance records
  const { data: insurancesData, isLoading: insurancesLoading } = useQuery({
    queryKey: [GET_CLIENT_INSURANCES, clientId],
    queryFn: () => getClientInsurancesAsync(clientId!),
    enabled: !!clientId,
    staleTime: STALE_TIME_5_MINUTES,
  });

  // Find the primary active insurance plan ID
  const insurances: any[] = insurancesData?.data ?? [];
  const primaryInsurance = insurances.find((i) => i.is_primary && i.active);
  const planId = primaryInsurance?.insurance_plan_id ?? null;

  // Step 2: Get the insurance plan (with CPT code IDs)
  const { data: planData, isLoading: planLoading } = useQuery({
    queryKey: [GET_CLIENT_INSURANCE_PLAN_BY_ID, planId],
    queryFn: () => getInsurancePlanByIdAsync(planId!),
    enabled: !!planId,
    staleTime: STALE_TIME_5_MINUTES,
  });

  const plan = planData?.data ?? null;

  // Step 3: Get all insurance codes for resolving IDs → code strings
  const hasCptIds =
    plan?.assessment_cpt_code_id ||
    plan?.direct_therapy_cpt_code_id ||
    plan?.caregiver_mediated_cpt_code_id ||
    plan?.caregiver_training_cpt_code_id ||
    plan?.protocol_modification_direct_cpt_code_id ||
    plan?.protocol_modification_indirect_cpt_code_id;

  const { data: codesData, isLoading: codesLoading } = useQuery({
    queryKey: [GET_ALL_INSURANCE_CODES],
    queryFn: () => getAllInsuranceCodesAsync(),
    enabled: !!hasCptIds,
    staleTime: STALE_TIME_60_MINUTES,
  });

  const allCodes: CptCodeInfo[] = codesData?.data ?? [];
  const resolveCode = (id: number | null | undefined): CptCodeInfo | null => {
    if (!id) return null;
    return allCodes.find((c) => c.id === id) ?? null;
  };

  // Only build a non-null cptCodes object when the plan has at least one CPT
  // code configured. If the plan exists but every resolved mapping is null,
  // treat it the same as "no plan" so ViewFields/EditFields fall back to
  // showing all rows (showAll = true) rather than hiding everything.
  const resolvedCptCodes: ClientInsurancePlanCptCodes | null =
    plan && hasCptIds
      ? {
          assessment: resolveCode(plan.assessment_cpt_code_id),
          direct_therapy: resolveCode(plan.direct_therapy_cpt_code_id),
          caregiver_mediated: resolveCode(plan.caregiver_mediated_cpt_code_id),
          caregiver_training: resolveCode(plan.caregiver_training_cpt_code_id),
          protocol_modification_direct: resolveCode(plan.protocol_modification_direct_cpt_code_id),
          protocol_modification_indirect: resolveCode(
            plan.protocol_modification_indirect_cpt_code_id
          ),
          approve_assessment_billing_at: plan.approve_assessment_billing_at ?? null,
        }
      : null;

  const cptCodes: ClientInsurancePlanCptCodes | null =
    resolvedCptCodes &&
    (resolvedCptCodes.assessment ||
      resolvedCptCodes.direct_therapy ||
      resolvedCptCodes.caregiver_mediated ||
      resolvedCptCodes.caregiver_training ||
      resolvedCptCodes.protocol_modification_direct ||
      resolvedCptCodes.protocol_modification_indirect)
      ? resolvedCptCodes
      : null;

  return {
    cptCodes,
    isLoading: insurancesLoading || planLoading || codesLoading,
  };
};

export default useGetClientInsurancePlanCptCodes;
